/**
 * mainStreamStore — the homepage timeline's own pointer store.
 *
 * Holds *only pointers* — an ordered array of `BlockId`s plus segment
 * bookkeeping. Envelope data lives in `blockCollection`, the shared
 * TanStack DB cache. `useList()` JOINS the stream's `blockIds` against
 * `blockCollection` (via an `inArray` filter on the live query) so
 * the homepage feed is **strictly** the union of:
 *
 *   1. Blocks the stream itself fetched (initial seed / fetchNextPage /
 *      jumpToDate / loadGap).
 *   2. Blocks the stream was explicitly told about
 *      (`prependCaptured` after a fresh capture, `includeBlockIds`
 *      from the pinned section's warm path).
 *
 * Side-loaded envelopes from other surfaces (search panel, smart-
 * collection tab, collection-detail view) sit alongside in
 * `blockCollection` but **do not** appear in the homepage feed — the
 * `inArray(m.block.id, blockIds)` filter excludes them. The previous
 * shape rendered the entire cache and was confusing: opening a
 * Collection would silently inject those blocks into the homepage
 * timeline ("why did Mar 2024 just appear in my feed?").
 *
 * Sort key: `surfaceAt`. Server, live query, and pagination cursor
 * all order on the same column — historically `orderBy: "capturedAt"`
 * silently mapped to `surfaceAt` on the server while the cursor used
 * the truthful `capturedAt`, so the cursor key and the sort key
 * diverged whenever a bulk reprocess pinned `surfaceAt` to a
 * timestamp far away from the original `capturedAt`. The result was
 * that scroll-load filtered with `capturedBefore: <some old date>`
 * against a `surfaceAt`-ordered query and silently skipped every
 * row whose capture predated that cursor but whose `surfaceAt` was
 * still ahead of the loaded window.
 *
 * Why this store exists separately from `blockStore`:
 *   `blockStore` / `blockCollection` is the cross-surface envelope
 *   cache; every screen writes into it (search results, smart
 *   collections, collection detail, card previews). The homepage
 *   timeline is one of many readers and must isolate its own
 *   pagination state from those side writes — the cursor needs to
 *   anchor on the dates the timeline itself fetched, and the
 *   rendered list needs to match.
 */
import { useState, useCallback, useEffect, useRef } from "react";
import { useLiveQuery, eq, inArray } from "@tanstack/react-db";
import { generateId } from "@shared/types/id";
import { listBlocks } from "@/api/query-api";
import { getInitialBlocksPromise } from "@/api/initial-blocks-api";
import { blockCollection, blockEnvelopesHydratedPromise } from "@/data/block-collection";
import { blockStore } from "@/data/block-store";
import { debounce, readStreamBlockIdsSnapshot, writeStreamBlockIdsSnapshot, } from "@/data/idb-persistence";
import { getPinSnapshot } from "@/state/pin-store";
// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const PAGE_SIZE = 30;
/**
 * First-paint page size. Must stay in sync with `BOOTSTRAP_BLOCK_LIMIT`
 * on the server (`production/api/src/api/bootstrap/routes.ts`) because
 * the seed reads bootstrap's `payload.blocks` and uses
 * `blocks.length >= MAX_INITIAL_LOAD` to decide if there's a next
 * page. Drift wastes bytes (server returns more than the client uses)
 * or stalls pagination (client immediately fires a redundant second
 * page).
 *
 * 30 covers the ~10 cards above the fold plus enough headroom that
 * the first scroll doesn't stall; `fetchNextPage` then runs on a
 * warm connection while the user is reading.
 */
const MAX_INITIAL_LOAD = 10;
const MAX_JUMP_LOAD = 100;
/**
 * How long to coalesce `blockIds` changes before snapshotting to
 * IndexedDB. A seed lands 30 ids in one tick; an unfurled stack
 * detail or a `fetchNextPage` lands ~30 more. We want one write per
 * burst, not one per id. Matches the cadence of
 * `block-collection.ts`'s envelope snapshot debouncer so the two
 * writes settle together — the persisted id list always lines up
 * with the persisted envelope set on the next mount.
 */
const IDB_PERSIST_DEBOUNCE_MS = 250;
// ---------------------------------------------------------------------------
// Module state
// ---------------------------------------------------------------------------
/**
 * The homepage Stream's loaded block ids. Order isn't display order
 * (the live query re-sorts by surfaceAt / capturedAt) — it's the
 * membership set the live query filters on AND the cursor / hasMore
 * are computed against.
 */
let blockIds = [];
/**
 * Bumped whenever `blockIds` changes. Used as a `useLiveQuery` dep
 * so the homepage's filtered query re-runs against the new id set
 * without us having to thread the array reference through React
 * state. All mutations to `blockIds` MUST go through the helpers
 * below so the version stays accurate — readers depend on it.
 */
let blockIdsVersion = 0;
/** Tracks which `typeFilter` `blockIds` was loaded under. Switching wipes both. */
let activeTypeKey = "";
/**
 * Has the initial-page fetch run for the active typeKey? Tracked
 * separately from `blockIds.length` because a placeholder from
 * `prependCaptured` can leave `blockIds.length === 1` on a screen that
 * never mounted the homepage stream — without this flag, the
 * initial-fetch effect would skip the seed and the homepage would
 * render only the placeholder.
 */
let seededTypeKey;
/**
 * Monotonic counter bumped every time `resetPagination()` wipes
 * module state. The `useList` hook subscribes to it via the listener
 * bus and feeds it into the seed effect's deps + the per-mount
 * "already kicked off" guard. Without this, a stale-refresh (the tab
 * coming back after ≥5 min hidden) wipes `seededTypeKey` and
 * `blockIds` but the seed effect never re-runs:
 *
 *   - The effect's deps `[typeKey, type]` haven't changed (filter is
 *     the same, the user just came back).
 *   - `initialLoadKickedOff.current` is a per-component ref still
 *     pinned to the previous `typeKey`, so even a forced re-render
 *     would short-circuit at the second guard.
 *
 * Result: homepage stuck on an empty live-query (`__nope__` filter
 * because `blockIds.length === 0`), no way to re-fetch without a
 * full page reload. Bumping this counter on every reset makes the
 * effect's deps change AND invalidates the per-mount token, so the
 * seed fires fresh.
 */
let resetGeneration = 0;
let segments = [];
/** Per gap key `${newerId}|${olderId}` — which directions are exhausted. */
const gapDirectionsExhausted = new Map();
let jumping = false;
let fetchNextLoading = false;
const listeners = [];
function notifyListeners() {
    for (const fn of listeners)
        fn();
}
function registerListener(fn) {
    listeners.push(fn);
    return () => {
        const i = listeners.indexOf(fn);
        if (i >= 0)
            listeners.splice(i, 1);
    };
}
// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------
/**
 * Replace the entire `blockIds` list. Used for type-filter switches
 * and `resetPagination`. Always bumps the version; callers handle
 * the listener notification themselves so they can batch other
 * state changes (segment wipe, etc.) into the same notify.
 */
function replaceBlockIds(next) {
    blockIds = next;
    blockIdsVersion++;
}
/**
 * Push `id` to the head of `blockIds` if not already present.
 * Returns `true` when the list actually changed so callers can
 * skip an unnecessary `notifyListeners` round-trip.
 */
function prependBlockIdIfNew(id) {
    if (blockIds.includes(id))
        return false;
    blockIds = [id, ...blockIds];
    blockIdsVersion++;
    return true;
}
/**
 * Append every id in `ids` that's not already present. Returns
 * `true` when at least one was added. Used by both the seed /
 * `fetchNextPage` ingest path AND the pinned-warm path so a single
 * helper stays the source of truth for "extend the homepage's
 * known id set".
 */
function appendBlockIdsIfNew(ids) {
    let added = false;
    const known = new Set(blockIds);
    for (const id of ids) {
        if (known.has(id))
            continue;
        known.add(id);
        blockIds.push(id);
        added = true;
    }
    if (added)
        blockIdsVersion++;
    return added;
}
/**
 * Drop a single id (no-op if it isn't there). Returns `true` when
 * the list actually changed.
 */
function removeBlockIdIfPresent(id) {
    const before = blockIds.length;
    blockIds = blockIds.filter((bid) => bid !== id);
    const changed = blockIds.length !== before;
    if (changed)
        blockIdsVersion++;
    return changed;
}
function gapKey(newerId, olderId) {
    return `${newerId}|${olderId}`;
}
function getGapState(newerId, olderId) {
    const k = gapKey(newerId, olderId);
    let s = gapDirectionsExhausted.get(k);
    if (!s) {
        s = { fromNewer: false, fromOlder: false };
        gapDirectionsExhausted.set(k, s);
    }
    return s;
}
/**
 * Bounds across the surfaceAt of a batch of envelopes. Used to grow
 * `BlockSegment.coveredFrom`/`coveredTo`. Tracking on `surfaceAt`
 * (not `capturedAt`) so segment ranges match the homepage's sort
 * key — a gap between two segments means a `surfaceAt` window the
 * timeline hasn't loaded yet, which is what `loadGap` will fill.
 */
function envelopeBounds(envelopes) {
    if (envelopes.length === 0)
        return undefined;
    let min = envelopes[0].block.surfaceAt;
    let max = min;
    for (const e of envelopes) {
        const t = e.block.surfaceAt;
        if (t < min)
            min = t;
        if (t > max)
            max = t;
    }
    return { min, max };
}
function pinnedBlockIdSet() {
    const out = new Set();
    for (const p of getPinSnapshot({ type: "account" }, "feed")) {
        if (p.type === "block")
            out.add(p.id);
    }
    return out;
}
/**
 * Oldest `surfaceAt` across the stream's own `blockIds`, excluding
 * pinned ids. Looks up each id via `blockStore.get` and skips ids the
 * cache no longer knows about (e.g. removed via `blockStore.delete`).
 *
 * This is the correct cursor for `fetchNextPage`: the server orders
 * by `surfaceAt` (it's the canonical timeline key), so the cursor
 * must be on the same column as the sort. Using `capturedAt` as the
 * cursor against a `surfaceAt`-ordered query silently skipped rows
 * whenever the two columns diverged — most visibly after a bulk
 * reprocess that pinned `surfaceAt` to the reprocess timestamp while
 * leaving each block's original `capturedAt` intact.
 *
 * Excludes envelopes the homepage timeline didn't fetch (only those
 * recorded in `blockIds`), so side-loaded envelopes from search /
 * collection-detail / smart-collection views can't drag the cursor.
 */
function oldestStreamSurfaceAt() {
    if (blockIds.length === 0)
        return undefined;
    const pinned = pinnedBlockIdSet();
    let min;
    for (const id of blockIds) {
        if (pinned.has(id))
            continue;
        const env = blockStore.get(id);
        if (!env)
            continue;
        const t = env.block.surfaceAt;
        if (min === undefined || t < min)
            min = t;
    }
    return min;
}
function createSegmentFromBatch(envelopes, partial) {
    const b = envelopeBounds(envelopes);
    if (!b)
        return undefined;
    return {
        id: generateId("candidate"),
        coveredFrom: b.min,
        coveredTo: b.max,
        olderHasMore: partial.olderHasMore,
        newerHasMore: partial.newerHasMore,
        loadingOlder: false,
        loadingNewer: false,
    };
}
function sortSegmentsNewestFirst(segs) {
    return [...segs].sort((a, b) => b.coveredTo.localeCompare(a.coveredTo));
}
function mergeOverlappingSegments() {
    if (segments.length <= 1)
        return;
    const sorted = [...segments].sort((a, b) => a.coveredFrom.localeCompare(b.coveredFrom));
    const out = [];
    for (const s of sorted) {
        const last = out[out.length - 1];
        if (!last) {
            out.push(s);
            continue;
        }
        if (s.coveredFrom <= last.coveredTo) {
            last.coveredTo = s.coveredTo > last.coveredTo ? s.coveredTo : last.coveredTo;
            last.coveredFrom = s.coveredFrom < last.coveredFrom ? s.coveredFrom : last.coveredFrom;
            last.olderHasMore = last.olderHasMore || s.olderHasMore;
            last.newerHasMore = last.newerHasMore || s.newerHasMore;
        }
        else {
            out.push(s);
        }
    }
    segments = sortSegmentsNewestFirst(out);
}
function findSegment(id) {
    return segments.find((s) => s.id === id);
}
function minCoveredFrom() {
    if (segments.length === 0)
        return undefined;
    return segments.reduce((acc, s) => (s.coveredFrom < acc ? s.coveredFrom : acc), segments[0].coveredFrom);
}
function extendSegmentWithBatch(seg, envelopes) {
    const b = envelopeBounds(envelopes);
    if (!b)
        return;
    if (b.min < seg.coveredFrom)
        seg.coveredFrom = b.min;
    if (b.max > seg.coveredTo)
        seg.coveredTo = b.max;
}
/**
 * Mirror envelopes into the shared `blockStore` cache and append any
 * new ids to the stream's id list. Centralizing this here keeps the
 * cache write and the id-list update on the same beat — a `fetchNextPage`
 * never lands envelopes in the cache without also extending the stream.
 */
function ingestEnvelopes(envelopes) {
    if (envelopes.length === 0)
        return;
    blockStore.upsertMany(envelopes);
    appendBlockIdsIfNew(envelopes.map((env) => env.block.id));
}
// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------
/**
 * Returns blocks for the homepage timeline plus pagination handles.
 *
 * The rendered list is JOINED against the stream's own `blockIds`
 * via an `inArray` filter on the live query. That means side writes
 * to `blockCollection` from other surfaces (search panel, smart-
 * collection tab, collection-detail view) sit in the cache but
 * **don't** show up in the homepage feed — only blocks the stream
 * itself fetched, captured (`prependCaptured`), or was told to
 * include (`includeBlockIds`, used by the pinned warm path) appear
 * here.
 *
 * The same `blockIds` set is used to compute `hasMore` and the
 * `fetchNextPage` cursor (see `oldestStreamSurfaceAt`), so render
 * and pagination agree on what counts as "in the feed".
 */
function useList(options = {}) {
    const { type } = options;
    const typeKey = type ?? "";
    const [, forceUpdate] = useState(0);
    const prevTypeRef = useRef(typeKey);
    // Mirror of `resetGeneration` into React state so the seed effect
    // below can include it as a dep. The listener subscription
    // refreshes this on every module-level notify; `setState` bails
    // out automatically when the value hasn't changed, so only an
    // actual `resetPagination()` causes a render here.
    const [seedGeneration, setSeedGeneration] = useState(() => resetGeneration);
    // Switching the type filter wipes the loaded id list. Today's UX
    // treats type-filtered views as independent stacks (no scroll
    // continuity between Image and All), so a wipe is the simplest
    // faithful port.
    useEffect(() => {
        if (prevTypeRef.current !== typeKey) {
            prevTypeRef.current = typeKey;
            activeTypeKey = typeKey;
            replaceBlockIds([]);
            segments = [];
            seededTypeKey = undefined;
            gapDirectionsExhausted.clear();
            notifyListeners();
        }
        else if (activeTypeKey !== typeKey) {
            activeTypeKey = typeKey;
        }
    }, [typeKey]);
    // Subscribe to module-level state changes (segment / blockIds /
    // jumping mutations from outside React) so the hook re-renders.
    // Also pulls the latest `resetGeneration` snapshot so a
    // `resetPagination()` re-fires the seed effect below — without
    // this, `forceUpdate` alone only refreshes derived values; the
    // effect's deps stay constant and the seed never re-runs.
    useEffect(() => registerListener(() => {
        setSeedGeneration(resetGeneration);
        forceUpdate((n) => n + 1);
    }), []);
    // The homepage feed is the intersection of `blockCollection` (the
    // shared envelope cache) with the stream's own `blockIds` (what
    // the homepage itself has loaded / been told about). Filtering
    // here — not just on the cursor — is what keeps an unrelated
    // collection-detail load from injecting old blocks into the home
    // timeline.
    //
    // `blockIdsVersion` is the dep that triggers the live query to
    // re-evaluate when the id set changes. The query reads `blockIds`
    // from the closure, so the latest snapshot is always picked up on
    // every re-evaluation.
    const { data: rawData, isLoading } = useLiveQuery((q) => {
        let query = q.from({ m: blockCollection });
        if (blockIds.length === 0) {
            // An unsatisfiable filter is the cleanest way to say "no
            // rows yet" — `inArray` on an empty list throws in some
            // engines, and we want a predictable empty result during
            // the brief window before the seed runs.
            query = query.where(({ m }) => eq(m.block.id, "__nope__"));
        }
        else {
            query = query.where(({ m }) => inArray(m.block.id, blockIds));
        }
        if (type) {
            query = query.where(({ m }) => eq(m.block.type, type));
        }
        return query.orderBy(({ m }) => m.block.surfaceAt, "desc");
    }, [type, blockIdsVersion]);
    const blocks = rawData ?? [];
    // Initial seed: if we haven't run the seed fetch yet for this
    // typeKey, do it now. The shared `blockCollection.queryFn` does its
    // own initial fetch for the cache, but the stream's id list has to
    // be built explicitly so unrelated cache writes don't leak into
    // pagination.
    //
    // Note: we gate on `seededTypeKey`, not `blockIds.length`, because
    // a placeholder from `prependCaptured` (e.g. user captured from a
    // collection screen before mounting the homepage) leaves the list
    // non-empty without ever having run the seed.
    //
    // The per-mount `initialLoadKickedOff` ref is tracked by a
    // composite token `${typeKey}\0${seedGeneration}`, not bare
    // `typeKey`, so a `resetPagination()` bump invalidates the previous
    // run's guard and the seed fires fresh. Without the generation in
    // the token, the ref stays pinned to the original typeKey across a
    // reset and short-circuits the re-seed permanently.
    const initialLoadKickedOff = useRef(undefined);
    const [initialLoading, setInitialLoading] = useState(false);
    useEffect(() => {
        if (seededTypeKey === typeKey)
            return;
        const kickoffToken = `${typeKey}\0${seedGeneration}`;
        if (initialLoadKickedOff.current === kickoffToken)
            return;
        initialLoadKickedOff.current = kickoffToken;
        setInitialLoading(true);
        void (async () => {
            try {
                // For the homepage's default (no type filter) we join the
                // `getInitialBlocksPromise()` singleton — same one
                // `blockCollection.queryFn` awaits, so the cold-load
                // `/v1/block/list?limit=30` fires exactly once even though
                // these two subscribers mount in different parts of the
                // React commit cycle.
                //
                // Type-filtered views go through `listBlocks` directly
                // because the singleton fetches the unfiltered top set; a
                // dedicated filtered fetch is the right path there.
                let blocks;
                if (type) {
                    const page = await listBlocks({
                        limit: MAX_INITIAL_LOAD,
                        orderBy: "surfaceAt",
                        order: "desc",
                        type,
                    });
                    blocks = page.blocks;
                }
                else {
                    blocks = await getInitialBlocksPromise();
                }
                // Type filter changed or another reset landed mid-flight —
                // drop the stale page.
                if (initialLoadKickedOff.current !== kickoffToken)
                    return;
                ingestEnvelopes(blocks);
                // Extend the existing top segment if one is present (i.e. the
                // IDB hydrator ran first and seeded `segments` from cached
                // envelopes' surfaceAt bounds), otherwise create a fresh one.
                // Overwriting unconditionally would lose the hydrated
                // segment's `coveredFrom` — the oldest cached `surfaceAt` —
                // and pagination would then re-fetch already-loaded older
                // blocks the next time the user scrolled, because
                // `oldestStreamSurfaceAt()` would have rolled forward to the
                // seed's batch bottom.
                const existingTop = segments[0];
                if (existingTop !== undefined) {
                    extendSegmentWithBatch(existingTop, blocks);
                    // A short seed batch (< MAX_INITIAL_LOAD) means the server
                    // returned fewer than a full page, which on the cold-load
                    // path would normally signal "no more older". But the
                    // hydrated segment may legitimately have older entries
                    // already covered — leave `olderHasMore` alone in that
                    // case and let `fetchNextPage` discover the real end.
                    if (blocks.length >= MAX_INITIAL_LOAD) {
                        existingTop.olderHasMore = true;
                    }
                    mergeOverlappingSegments();
                }
                else {
                    const seg = createSegmentFromBatch(blocks, {
                        olderHasMore: blocks.length >= MAX_INITIAL_LOAD,
                        newerHasMore: false,
                    });
                    if (seg) {
                        segments = [seg];
                        mergeOverlappingSegments();
                    }
                }
                seededTypeKey = typeKey;
            }
            finally {
                if (initialLoadKickedOff.current === kickoffToken) {
                    setInitialLoading(false);
                }
                notifyListeners();
            }
        })();
    }, [typeKey, type, seedGeneration]);
    const fetchNextPage = useCallback(async () => {
        if (fetchNextLoading)
            return;
        // Cursor is derived from the stream's own `blockIds` — the blocks
        // the timeline actually fetched — never from `blockCollection` at
        // large. This is the whole point of the refactor: a search /
        // smart-collection write to the cache must not be able to drag the
        // "oldest known timestamp" backwards into a region the timeline
        // never asked about, which would stall the next page request.
        //
        // Cursor key (`surfaceAt`) matches the server's `orderBy` so the
        // `surfaceBefore` filter slices the timeline at the same column
        // it's sorted on. Using a different key (e.g. `capturedAt`)
        // produces a cursor unrelated to where the result set is being
        // sliced and silently skips rows whose `capturedAt` is greater
        // than the cursor but whose `surfaceAt` is less.
        const cursor = oldestStreamSurfaceAt() ?? minCoveredFrom();
        if (!cursor)
            return;
        const tail = segments.reduce((best, s) => (s.coveredFrom < best.coveredFrom ? s : best), segments[0]);
        if (!tail || !tail.olderHasMore)
            return;
        fetchNextLoading = true;
        notifyListeners();
        try {
            const page = await listBlocks({
                limit: PAGE_SIZE,
                surfaceBefore: cursor,
                orderBy: "surfaceAt",
                order: "desc",
                type,
            });
            if (page.blocks.length === 0) {
                tail.olderHasMore = false;
            }
            else {
                ingestEnvelopes(page.blocks);
                extendSegmentWithBatch(tail, page.blocks);
                tail.olderHasMore = page.blocks.length >= PAGE_SIZE;
                mergeOverlappingSegments();
            }
        }
        finally {
            fetchNextLoading = false;
            notifyListeners();
        }
    }, [type]);
    // Until the seed runs we conservatively report `hasMore = true` so
    // the Stream renders its load-more sentinel; once segments exist
    // we trust their `olderHasMore` flags. After the seed completes
    // and `seededTypeKey === typeKey`, `segments.length === 0` only
    // happens for an empty account — in which case `hasMore` is false.
    const needsSeed = seededTypeKey !== typeKey;
    const hasMore = needsSeed ? true : segments.some((s) => s.olderHasMore);
    // The spinner only paints when there is genuinely nothing to show.
    // With IDB hydration of `blockIds` in place (see
    // `hydrateStreamFromIdb` below), a warm reload populates
    // `blockIds` from the persisted snapshot BEFORE the seed network
    // call resolves — so the user sees cached cards instantly while
    // the seed runs in the background and merges fresher envelopes in
    // at the top via the `surfaceAt desc` ordering. Gating on
    // `blocks.length === 0` (the live-query result, post-`inArray`
    // filter) covers the equivalent "blocks not addressable yet"
    // window during the brief sliver where `blockIds` is populated
    // but `blockCollection` hasn't finished its own IDB replay.
    //
    // Cold-load (empty IDB) falls back to the original behaviour: no
    // cached blocks → `blocks.length === 0` → spinner shows until the
    // seed lands.
    //
    // The wrapper `(isLoading || initialLoading || needsSeed)` reads
    // the same "is the data source warming up?" signal as before — we
    // just AND it with "and we have nothing to show yet", so cached
    // cards aren't hidden by a transient warming state.
    const isWarming = isLoading || initialLoading || needsSeed;
    return {
        blocks,
        isLoading: isWarming && blocks.length === 0,
        hasMore,
        isFetchingNextPage: fetchNextLoading,
        fetchNextPage,
    };
}
function useSegments() {
    const [snap, setSnap] = useState(() => [...segments]);
    useEffect(() => registerListener(() => {
        setSnap([...segments]);
    }), []);
    return snap;
}
function getSegments() {
    return [...segments];
}
function isGapDirectionExhausted(newerId, olderId, direction) {
    const g = getGapState(newerId, olderId);
    return direction === "fromNewer" ? g.fromNewer : g.fromOlder;
}
function isJumping() {
    return jumping;
}
function useJumping() {
    const [v, setV] = useState(() => jumping);
    useEffect(() => {
        setV(jumping);
        return registerListener(() => {
            setV(jumping);
        });
    }, []);
    return v;
}
// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------
/**
 * Append a freshly-captured block id to the stream so it appears at the
 * top of the timeline immediately. Also extends the top segment's
 * coveredTo so timeline-gap rendering stays correct.
 *
 * Takes `surfaceAt` (which equals `capturedAt` on first insert per
 * `BlockEntity.create`) so the bound matches the column segments
 * track. Callers passing the placeholder envelope can use either —
 * placeholders set `surfaceAt = capturedAt` by construction.
 *
 * Called from `blockStore.add` once the polling loop hands back the
 * real server envelope.
 */
function prependCaptured(id, surfaceAt) {
    prependBlockIdIfNew(id);
    const ordered = sortSegmentsNewestFirst(segments);
    const top = ordered[0];
    if (top && surfaceAt > top.coveredTo) {
        top.coveredTo = surfaceAt;
    }
    notifyListeners();
}
/**
 * Register block ids the homepage should display even though it
 * didn't fetch them itself. Used by the pinned section's warm path:
 * pinned envelopes land in `blockCollection` via
 * `pinnedStreamStore.warm`, but without this hook they wouldn't pass
 * the `useList` filter and the pinned section would render empty
 * even though the data is in cache.
 *
 * Idempotent: ids already in `blockIds` are skipped, so repeated
 * pin/unpin/pin cycles don't grow the list unbounded. Cursor logic
 * already excludes pinned ids from `oldestStreamSurfaceAt`, so
 * including them here doesn't drag pagination into stale dates.
 */
function includeBlockIds(ids) {
    if (ids.length === 0)
        return;
    const branded = ids.map((id) => id);
    if (appendBlockIdsIfNew(branded))
        notifyListeners();
}
/**
 * Drop a block id from the stream. Called from `blockStore.remove` so
 * the timeline updates in lockstep with the cache delete. Segments are
 * intentionally not adjusted — a deleted block shrinks the visible feed
 * but doesn't change which time window we've loaded.
 */
function removeId(id) {
    if (removeBlockIdIfPresent(id))
        notifyListeners();
}
/**
 * Load every block surfaced in the given window. The arg names are
 * legacy — they bound the `surfaceAt` window the user is jumping to,
 * so the loaded segment lines up with the homepage's `surfaceAt`
 * sort key. The user-facing "jump to March" semantics on the
 * homepage timeline is "scroll the timeline (sorted by surfaceAt) to
 * the position where surfaceAt = March". Callers wanting truthful
 * `capturedAt`-window jumps would need a separate code path.
 */
async function jumpToDate(surfaceAfter, surfaceBefore, type) {
    jumping = true;
    notifyListeners();
    try {
        const page = await listBlocks({
            limit: MAX_JUMP_LOAD,
            surfaceAfter,
            surfaceBefore,
            orderBy: "surfaceAt",
            order: "desc",
            type,
        });
        const newSeg = createSegmentFromBatch(page.blocks, {
            olderHasMore: page.blocks.length >= MAX_JUMP_LOAD,
            newerHasMore: true,
        });
        if (page.blocks.length > 0) {
            ingestEnvelopes(page.blocks);
            if (newSeg) {
                segments.push(newSeg);
                mergeOverlappingSegments();
            }
        }
    }
    finally {
        jumping = false;
        notifyListeners();
    }
}
async function loadGap(input, type) {
    const { newerSegmentId, olderSegmentId, direction } = input;
    const newerSeg = findSegment(newerSegmentId);
    const olderSeg = findSegment(olderSegmentId);
    if (!newerSeg || !olderSeg)
        return;
    const g = getGapState(newerSegmentId, olderSegmentId);
    if (direction === "fromNewer" && g.fromNewer)
        return;
    if (direction === "fromOlder" && g.fromOlder)
        return;
    if (direction === "fromNewer") {
        newerSeg.loadingNewer = true;
    }
    else {
        olderSeg.loadingOlder = true;
    }
    notifyListeners();
    try {
        const page = direction === "fromNewer"
            ? await listBlocks({
                limit: PAGE_SIZE,
                surfaceBefore: newerSeg.coveredFrom,
                surfaceAfter: olderSeg.coveredTo,
                orderBy: "surfaceAt",
                order: "desc",
                type,
            })
            : await listBlocks({
                limit: PAGE_SIZE,
                surfaceAfter: olderSeg.coveredTo,
                surfaceBefore: newerSeg.coveredFrom,
                orderBy: "surfaceAt",
                order: "asc",
                type,
            });
        if (page.blocks.length === 0) {
            if (direction === "fromNewer")
                g.fromNewer = true;
            else
                g.fromOlder = true;
        }
        else {
            ingestEnvelopes(page.blocks);
            if (direction === "fromNewer") {
                extendSegmentWithBatch(newerSeg, page.blocks);
                if (page.blocks.length < PAGE_SIZE)
                    g.fromNewer = true;
            }
            else {
                extendSegmentWithBatch(olderSeg, page.blocks);
                if (page.blocks.length < PAGE_SIZE)
                    g.fromOlder = true;
            }
            mergeOverlappingSegments();
        }
    }
    finally {
        if (direction === "fromNewer") {
            const s = findSegment(newerSegmentId);
            if (s)
                s.loadingNewer = false;
        }
        else {
            const s = findSegment(olderSegmentId);
            if (s)
                s.loadingOlder = false;
        }
        notifyListeners();
    }
}
function resetPagination() {
    replaceBlockIds([]);
    segments = [];
    seededTypeKey = undefined;
    gapDirectionsExhausted.clear();
    fetchNextLoading = false;
    // Bump BEFORE notifying so subscribers see the new generation in
    // the same snapshot they observe the wiped state — otherwise the
    // listener callback in `useList` would re-render against an
    // unchanged `seedGeneration` and the seed effect wouldn't re-fire.
    //
    // Note: an earlier shape of this function also fired
    // `queryClient.invalidateQueries({ queryKey: ["blocks", "collection"] })`
    // to nudge `blockCollection`'s React-Query queryFn into re-firing.
    // `blockCollection` no longer has a queryFn (see its file header), so
    // that call became a no-op and was removed. Fresh blocks now land via
    // the seed effect's `getInitialBlocksPromise()` → `ingestEnvelopes`
    // path, which `resetGeneration++` re-triggers below.
    resetGeneration++;
    notifyListeners();
}
// ---------------------------------------------------------------------------
// IDB persistence — homepage stream block-ids
//
// The homepage feed is the intersection of `blockCollection` (the
// shared envelope cache, hydrated by `block-collection.ts` from IDB
// on module load) with `mainStreamStore.blockIds` (the strict
// "homepage-loaded" pointer set). On a warm reload `blockCollection`
// already has envelopes ready to paint, but until `blockIds` is
// populated the `inArray(...)` filter excludes them and the user
// sees the seed-loading spinner sitting on top of a fully populated
// cache.
//
// Persisting `blockIds` alongside the envelope snapshot lets a warm
// reload paint cached cards in the same ~20 ms as the envelope
// hydrate, while the network seed below runs in the background and
// merges fresher envelopes in at the top via the `surfaceAt desc`
// live-query sort. New captures naturally appear above older cached
// ones — no special "prepend animation" needed — because the live
// query re-sorts whenever `blockCollection` emits.
//
// The hydration body waits for `blockEnvelopesHydratedPromise` so
// the envelopes are addressable before we expose the id list (no
// `[]` flash from "ids in, envelopes still in flight"). It also
// skips when `blockIds` is already non-empty — guards against a
// late hydration racing the seed (which always wins) or
// `prependCaptured` (a local placeholder shouldn't be displaced by
// stale cached ids).
// ---------------------------------------------------------------------------
async function hydrateStreamFromIdb() {
    try {
        await blockEnvelopesHydratedPromise;
        if (blockIds.length > 0)
            return;
        const cachedIds = await readStreamBlockIdsSnapshot();
        if (cachedIds.length === 0)
            return;
        if (blockIds.length > 0)
            return;
        // Drop any persisted ids whose envelopes no longer live in the
        // hydrated cache (envelope-snapshot truncation, schema bump,
        // server-side delete that ran while the tab was closed). The
        // remainder are guaranteed to render — `blockStore.useById`
        // resolves them synchronously — so the user never sees pending
        // skeletons on the hydrated cards. Anything we drop here can
        // still come back via the seed below if the server still has
        // it.
        const validIds = [];
        const validEnvelopes = [];
        for (const id of cachedIds) {
            const env = blockStore.get(id);
            if (env !== undefined) {
                validIds.push(id);
                validEnvelopes.push(env);
            }
        }
        if (validIds.length === 0)
            return;
        blockIds = validIds;
        blockIdsVersion++;
        // Build a single top segment from the cached envelopes' bounds
        // so `fetchNextPage` has a cursor to anchor on and the
        // timeline-gap UI (which reads `segments`) has something to
        // render against. `olderHasMore: true` is conservative — at
        // worst the next page fetch returns empty and we flip it false
        // there. The seed effect below extends this same segment with
        // its own batch instead of overwriting it.
        const seg = createSegmentFromBatch(validEnvelopes, {
            olderHasMore: true,
            newerHasMore: false,
        });
        if (seg !== undefined) {
            segments = [seg];
            mergeOverlappingSegments();
        }
        notifyListeners();
    }
    catch (e) {
        console.warn("[main-stream-store] IDB hydrate failed:", e);
    }
}
void hydrateStreamFromIdb();
/**
 * Snapshot writer. Subscribed to module-level listener notifications
 * so any path that mutates `blockIds` (seed, fetchNextPage,
 * prependCaptured, includeBlockIds, removeId, loadGap, jumpToDate)
 * eventually persists. Debounced because a single seed lands ~30
 * ids in one tick — we want one write per burst, not 30.
 *
 * Only the default (no type filter) view persists. Type-filtered
 * views are session-local by convention (see the comment on
 * `activeTypeKey`); persisting them would mean stashing one
 * snapshot per filter, which doesn't fit the "fast-path optimization"
 * model.
 *
 * Empty writes are skipped inside `writeStreamBlockIdsSnapshot`
 * itself so a transient `resetPagination()` wipe (stale-refresh) or
 * a type-filter switch doesn't clobber the previous good snapshot.
 * Clearing the snapshot has its own dedicated path
 * (`clearStreamBlockIdsSnapshot`) for the sign-out case.
 */
const flushStreamBlockIdsSnapshot = debounce(() => {
    if (activeTypeKey !== "")
        return;
    void writeStreamBlockIdsSnapshot(blockIds);
}, IDB_PERSIST_DEBOUNCE_MS);
registerListener(() => {
    flushStreamBlockIdsSnapshot();
});
// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------
export const mainStreamStore = {
    useList,
    useSegments,
    getSegments,
    useJumping,
    isJumping,
    isGapDirectionExhausted,
    resetPagination,
    prependCaptured,
    includeBlockIds,
    removeId,
    jumpToDate,
    loadGap,
    registerListener,
};
